/**
 * SegmentDetail — drilldown view shown after clicking a SegmentCard.
 * Renders header (icon + name + description + status), a 5-starter
 * grid, and the planned-connectors list. Clicking a starter navigates
 * to /spark with prefill query params.
 */

import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/utils/cn';
import type { Segment, SegmentConnector } from '@/data/segments';

import { StarterCard } from './StarterCard';
import { StatusBadge } from './SegmentCard';

export function SegmentDetail({
  segment,
  onBack,
}: {
  segment: Segment;
  onBack: () => void;
}) {
  const { t } = useTranslation(['segments', 'common']);
  const Icon = segment.Icon;
  const ready = segment.status === 'ready';

  return (
    <div className="flex flex-col gap-6" data-testid={`segment-detail-${segment.id}`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} leading={<ArrowLeft className="h-4 w-4" />}>
          {t('detail.back_to_categories')}
        </Button>
      </div>

      <header className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-6 sm:flex-row sm:items-start">
        <span
          className={cn(
            'inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-lg',
            ready ? 'bg-spark-soft text-spark' : 'bg-surface-raised text-ink-muted',
          )}
          aria-hidden
        >
          <Icon className="h-7 w-7" strokeWidth={1.8} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={segment.status} />
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
              {t(`segments:tier.t${segment.tier}_label`)}
            </span>
            {segment.adapterCrate ? (
              <span className="font-mono text-[10px] text-ink-subtle">
                {segment.adapterCrate}
              </span>
            ) : null}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            {t(`segments:segments.${segment.id}.name`)}
          </h1>
          <p className="text-sm text-ink-muted">
            {t(`segments:segments.${segment.id}.description`)}
          </p>
        </div>
      </header>

      <section aria-labelledby={`starters-heading-${segment.id}`} className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2
            id={`starters-heading-${segment.id}`}
            className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
          >
            {t('detail.starters_heading')}
          </h2>
          <span className="text-xs text-ink-subtle">
            {t('detail.starters_subhead')}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {segment.starters.map((starter) => (
            <StarterCard key={starter.id} segment={segment} starter={starter} />
          ))}
        </div>
      </section>

      <section aria-labelledby={`connectors-heading-${segment.id}`} className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between">
          <h2
            id={`connectors-heading-${segment.id}`}
            className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
          >
            {t('detail.connectors_heading')}
          </h2>
          <span className="text-xs text-ink-subtle">
            {t('detail.connectors_subhead', { count: segment.connectors.length })}
          </span>
        </div>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3" role="list">
          {segment.connectors.map((c) => (
            <ConnectorRow key={c.id} connector={c} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function ConnectorRow({ connector }: { connector: SegmentConnector }) {
  const { t } = useTranslation('segments');
  const planned = connector.status === 'planned';
  // Show the connector's display name if i18n has it, otherwise the
  // raw id with underscores rendered as spaces — this keeps unmapped
  // connectors readable without breaking the UI.
  const label = t(`connectors.${connector.id}`, {
    defaultValue: connector.id.replace(/_/g, ' '),
  });
  return (
    <li
      className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2"
      data-status={connector.status}
    >
      <span className="truncate text-sm text-ink">{label}</span>
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
          planned
            ? 'bg-surface-raised text-ink-subtle ring-1 ring-border'
            : 'bg-success/10 text-success ring-1 ring-success/30',
        )}
      >
        {t(`connector_status.${planned ? 'planned' : 'available'}`)}
      </span>
    </li>
  );
}
